import React, { useState, useEffect } from 'react';
import { CellSelection } from '@tiptap/pm/tables';

const TablePopup = ({ editor, position, onClose, hasSelection, selectionData }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const popupStyle = {
    position: 'fixed',
    top: position.y,
    left: position.x,
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    zIndex: 10000,
    minWidth: '180px',
    padding: '4px 0',
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'scale(1)' : 'scale(0.95)',
    transition: 'all 0.15s ease-out'
  };

  const buttonStyle = {
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#333',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'background-color 0.15s',
    width: '100%',
    border: 'none',
    background: 'transparent',
    textAlign: 'left'
  };

  const separatorStyle = {
    height: '1px',
    backgroundColor: '#e0e0e0',
    margin: '4px 0'
  };

  const handleClick = (action) => {
    console.log('TablePopup handleClick:', action, 'hasSelection:', hasSelection, 'selectionData:', selectionData);
    
    // Восстанавливаем выделение перед выполнением команды merge
    if (action === 'mergeCells' && selectionData) {
      try {
        const { state } = editor;
        const tr = state.tr;
        
        // Проверяем, есть ли выделение нескольких ячеек
        if (selectionData.hasSelection && selectionData.from !== selectionData.to) {
          // Создаем CellSelection для табличных ячеек
          const newSelection = CellSelection.create(
            state.doc, 
            selectionData.anchor, 
            selectionData.head
          );
          
          tr.setSelection(newSelection);
          editor.view.dispatch(tr);
          
          // Небольшая задержка для стабилизации выделения
          setTimeout(() => {
            console.log('Selection restored successfully, executing merge command');
            editor.chain().focus().mergeCells().run();
            onClose();
          }, 50);
          
          return; // Выходим из функции, так как команда будет выполнена в setTimeout
        } else {
          // Если нет выделения нескольких ячеек, просто выполняем команду
          console.log('No multiple cell selection, executing merge command directly');
          editor.chain().focus().mergeCells().run();
          onClose();
        }
      } catch (error) {
        console.log('Error restoring selection:', error);
        // Если не удалось восстановить выделение, пробуем выполнить команду без него
        console.log('Trying to execute merge without selection restoration');
        editor.chain().focus().mergeCells().run();
        onClose();
      }
    }

    // Если нет выделения, выполняем команду сразу
    switch (action) {
      case 'insertRowAbove':
        editor.chain().focus().addRowBefore().run();
        break;
      case 'insertRowBelow':
        editor.chain().focus().addRowAfter().run();
        break;
      case 'insertColumnLeft':
        editor.chain().focus().addColumnBefore().run();
        break;
      case 'insertColumnRight':
        editor.chain().focus().addColumnAfter().run();
        break;
      case 'deleteRow':
        editor.chain().focus().deleteRow().run();
        break;
      case 'deleteColumn':
        editor.chain().focus().deleteColumn().run();
        break;
      case 'deleteTable':
        editor.chain().focus().deleteTable().run();
        break;
      case 'mergeCells':
        // Для merge cells без выделения ничего не делаем
        console.log('Merge cells requires selection');
        break;
      default:
        // Неизвестное действие
        break;
    }
    onClose();
  };

  // Используем переданное значение выделения вместо проверки текущего состояния
  const hasMultipleSelection = () => {
    // Проверяем, есть ли выделение нескольких ячеек
    const hasMultipleCells = selectionData && selectionData.hasSelection && selectionData.from !== selectionData.to;
    const hasValidSelection = selectionData && (selectionData.hasSelection || selectionData.isInTableCell);
    const result = hasMultipleCells || hasValidSelection;
    console.log('hasMultipleSelection check:', { 
      hasSelection, 
      selectionData, 
      hasMultipleCells,
      hasValidSelection,
      result 
    });
    return result;
  };

  return (
    <div style={popupStyle}>
      <button
        style={buttonStyle}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        onClick={() => handleClick('insertRowAbove')}
      >
        <span style={{ fontSize: '12px', color: '#666' }}>↑</span> Insert row above
      </button>
      
      <button
        style={buttonStyle}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        onClick={() => handleClick('insertRowBelow')}
      >
        <span style={{ fontSize: '12px', color: '#666' }}>↓</span> Insert row below
      </button>
      
      <div style={separatorStyle}></div>
      
      <button
        style={buttonStyle}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        onClick={() => handleClick('insertColumnLeft')}
      >
        <span style={{ fontSize: '12px', color: '#666' }}>←</span> Insert column left
      </button>
      
      <button
        style={buttonStyle}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        onClick={() => handleClick('insertColumnRight')}
      >
        <span style={{ fontSize: '12px', color: '#666' }}>→</span> Insert column right
      </button>
      
      <div style={separatorStyle}></div>
      
      <button
        style={buttonStyle}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        onClick={() => handleClick('deleteRow')}
      >
        <span style={{ fontSize: '12px', color: '#999' }}>×</span> Delete row
      </button>
      
      <button
        style={buttonStyle}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        onClick={() => handleClick('deleteColumn')}
      >
        <span style={{ fontSize: '12px', color: '#999' }}>×</span> Delete column
      </button>
      
      <div style={separatorStyle}></div>
      
      <button
        style={{...buttonStyle, color: '#e74c3c'}}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#ffe5e5'}
        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        onClick={() => handleClick('deleteTable')}
      >
        <span style={{ fontSize: '12px' }}>🗑</span> Delete table
      </button>
      
      <div style={separatorStyle}></div>
      <button
        style={{
          ...buttonStyle,
          color: hasMultipleSelection() ? '#333' : '#999',
          cursor: hasMultipleSelection() ? 'pointer' : 'not-allowed'
        }}
        onMouseEnter={(e) => {
          if (hasMultipleSelection()) {
            e.target.style.backgroundColor = '#f5f5f5';
          }
        }}
        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        onClick={() => {
          if (hasMultipleSelection()) {
            handleClick('mergeCells');
          }
        }}
        title={hasMultipleSelection() ? 'Merge selected cells' : 'Select multiple cells to merge'}
      >
        <span style={{ 
          fontSize: '12px', 
          color: hasMultipleSelection() ? '#666' : '#ccc' 
        }}>⊞</span> Merge cells
      </button>
    </div>
  );
};

export default TablePopup; 